import React from 'react';
import QueryConsole from '../QueryConsole';
import { useHOA } from '../../../contexts/HOAContext';
import { useAuth } from '../../../contexts/AuthContext';
import { Card, SectionHeader, MaterialIcon } from '../../ui';
import SessionFeed from './SessionFeed';
import PeopleRoster from './PeopleRoster';

const FOCUS_REFETCH_MIN_MS = 60 * 1000;

const VIEWS = [
  { key: 'people', label: 'People', icon: 'group' },
  { key: 'sessions', label: 'Sessions', icon: 'history' },
  { key: 'search', label: 'Query Console', icon: 'auto_awesome' },
];

const TogglePill = ({ on, onClick, iconOn, iconOff, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={on}
    className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border text-xs font-bold transition-all ${
      on ? 'bg-primary-dark text-white border-primary-dark' : 'bg-white text-primary-dark border-amber-100/60 hover:border-secondary/40'
    }`}
  >
    <MaterialIcon name={on ? iconOn : iconOff} size={16} />
    {children}
  </button>
);

/**
 * Admin Activity Log. Data comes from admin-only analytics RPCs; it loads on
 * open, on Refresh, and when the window regains focus (at most once a minute).
 */
const ActivityLogPanel = () => {
  const { hoa } = useHOA();
  const { user } = useAuth();
  const [view, setView] = React.useState('people');
  const [includeAdmin, setIncludeAdmin] = React.useState(false);
  const [includeArchived, setIncludeArchived] = React.useState(false);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [questionsPerson, setQuestionsPerson] = React.useState(null);
  const lastRefreshRef = React.useRef(Date.now());

  const refresh = React.useCallback(() => {
    lastRefreshRef.current = Date.now();
    setRefreshKey((k) => k + 1);
  }, []);

  React.useEffect(() => {
    const onFocus = () => {
      if (Date.now() - lastRefreshRef.current >= FOCUS_REFETCH_MIN_MS) refresh();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const isAnalyticsView = view !== 'search';

  return (
    <Card>
      <div className="flex flex-col gap-4 mb-6 border-b border-amber-100/40 pb-5">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <SectionHeader icon="pulse" title="Activity Logs" color="text-secondary" accent="bg-secondary/10" />
          <div className="flex flex-wrap bg-section-bg p-1.5 rounded-full border border-amber-100/40 gap-1" role="tablist" aria-label="Activity views">
            {VIEWS.map((v) => (
              <button
                key={v.key}
                type="button"
                role="tab"
                aria-selected={view === v.key}
                onClick={() => setView(v.key)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  view === v.key ? 'bg-white text-primary-dark warm-tactile-shadow' : 'text-slate-500 hover:text-primary-dark'
                }`}
              >
                <MaterialIcon name={v.icon} size={16} /> {v.label}
              </button>
            ))}
          </div>
        </div>

        {/* The toggles apply to every view, including the Query Console's review queue. */}
        <div className="flex flex-wrap items-center gap-2">
            <TogglePill on={includeAdmin} onClick={() => setIncludeAdmin((v) => !v)} iconOn="visibility" iconOff="visibility_off">
              Show admin &amp; test
            </TogglePill>
            <TogglePill on={includeArchived} onClick={() => setIncludeArchived((v) => !v)} iconOn="inventory_2" iconOff="inventory_2">
              Include archived
            </TogglePill>
            {isAnalyticsView && (
              <button
                type="button"
                onClick={refresh}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-amber-100/60 bg-white text-xs font-bold text-primary-dark hover:border-secondary/40 transition-all"
              >
                <MaterialIcon name="refresh" size={16} /> Refresh
              </button>
            )}
          </div>
      </div>

      {view === 'people' && (
        <PeopleRoster
          hoaId={hoa?.id}
          includeAdmin={includeAdmin}
          includeArchived={includeArchived}
          refreshKey={refreshKey}
          onOpenQuestions={(person) => {
            setQuestionsPerson(person);
            setView('search');
          }}
        />
      )}

      {view === 'sessions' && (
        <SessionFeed
          hoaId={hoa?.id}
          includeAdmin={includeAdmin}
          includeArchived={includeArchived}
          refreshKey={refreshKey}
        />
      )}

      {view === 'search' && (
        <div className="border border-amber-100/40 rounded-2xl overflow-hidden h-[600px] md:h-[650px] bg-white">
          <QueryConsole
            currentUser={user || { id: 'admin', role: 'admin', name: 'Administrator' }}
            includeAdmin={includeAdmin}
            includeArchived={includeArchived}
            personFilter={questionsPerson}
            onClearPersonFilter={() => setQuestionsPerson(null)}
          />
        </div>
      )}
    </Card>
  );
};

export default ActivityLogPanel;
